export type GuideSurfaceSection = 'money' | 'home' | 'health' | 'family' | 'work';

export const GUIDE_SURFACE_ORDER: GuideSurfaceSection[] = ['money', 'home', 'health', 'family', 'work'];

export const GUIDE_HUB_META = {
  hubSubtitle: 'Authoritative guides, support cards, and linked next-step routes across Adulting.',
  helperPacksTitle: 'Helper Packs',
  helperPacksSubtitle: 'Step-by-step guides for the bigger life-admin moments that need more than a quick note.',
  supportAreasTitle: 'Support Areas',
  supportAreasSubtitle: 'Browse by topic if you already know the area you need help with.',
  supportCardsTitle: 'Support Cards',
  supportCardsSubtitle: 'Short notes, official links, and calmer next steps for this area.',
} as const;

export const GUIDE_SURFACE_META: Record<
  GuideSurfaceSection,
  {
    label: string;
    emoji: string;
    hubTitle: string;
    supportAreaDescription: string;
    helperPackSubtitle: string;
    guidesSubtitle: string;
  }
> = {
  money: {
    label: 'Money',
    emoji: '💰',
    hubTitle: 'Money support',
    supportAreaDescription: 'Benefits, debt, budgeting, bills, and urgent money pressure support.',
    helperPackSubtitle: 'Guides, scripts, and official routes for budgeting, debt pressure, benefits, and the money moments that get big fast.',
    guidesSubtitle: 'All your money support guides, cards, and linked next-step routes in one place.',
  },
  home: {
    label: 'Home',
    emoji: '🏠',
    hubTitle: 'Home support',
    supportAreaDescription: 'Repairs, moving, renewals, and the home admin that quickly spills everywhere.',
    helperPackSubtitle: 'Guides, rights routes, and practical help for repairs, moving, renewals, and the home admin that spills everywhere.',
    guidesSubtitle: 'All your home support guides, cards, and next-step admin routes in one place.',
  },
  health: {
    label: 'Health',
    emoji: '🩺',
    hubTitle: 'Health support',
    supportAreaDescription: 'NHS admin, appointments, crisis routes, and keeping health tasks visible.',
    helperPackSubtitle: 'Guides, calm next steps, and official routes for NHS admin, crisis support, and keeping health tasks visible.',
    guidesSubtitle: 'All your health support guides, cards, and NHS/admin routes in one place.',
  },
  family: {
    label: 'My People',
    emoji: '🫶',
    hubTitle: 'People support',
    supportAreaDescription: 'School, childcare, caring, co-parenting, separation, and family admin that lives in your head.',
    helperPackSubtitle: 'Guides, scripts, and support for childcare, school admin, caring, separation, and the work that lives in your head.',
    guidesSubtitle: 'All your family and care support guides, cards, and linked next-step routes.',
  },
  work: {
    label: 'Work',
    emoji: '💼',
    hubTitle: 'Work support',
    supportAreaDescription: 'Rights at work, deadlines, self-employment, and difficult work moments that need a calm plan.',
    helperPackSubtitle: 'Guides, rights routes, and scripts for self-employment admin and difficult work moments that need a calm plan.',
    guidesSubtitle: 'All your work support guides, cards, rights routes, and self-employed next steps in one place.',
  },
};

export const GUIDE_SURFACE_BUCKETS: Record<GuideSurfaceSection, string[]> = {
  money: ['money', 'benefits-info'],
  home: ['home', 'tenant-rights', 'home-rights'],
  health: ['health', 'health-info'],
  family: ['family'],
  work: ['work', 'employment-rights', 'work-self-employed'],
};

export const GUIDE_ALL_BUCKETS = [...new Set(Object.values(GUIDE_SURFACE_BUCKETS).flat())];

export function getGuideBuckets(section?: GuideSurfaceSection | null) {
  if (!section) {
    return GUIDE_ALL_BUCKETS;
  }

  return GUIDE_SURFACE_BUCKETS[section];
}

export function getGuideSurfaceSection(bucket: string): GuideSurfaceSection | null {
  const matchingSection = GUIDE_SURFACE_ORDER.find((section) => GUIDE_SURFACE_BUCKETS[section].includes(bucket));
  return matchingSection || null;
}

export type GuideRailSummaryStepLike = {
  title: string;
  description?: string | null;
  callScript?: string | null;
  call_script?: string | null;
};

export type GuideRailSummaryItemLike = {
  id: string;
  kind: 'playbook' | 'content-card';
  title: string;
  description?: string | null;
  summary?: string | null;
  body?: string | null;
  steps?: GuideRailSummaryStepLike[];
};

export type GuideRailLaunchSummary = {
  itemId: string;
  itemKind: 'playbook' | 'content-card';
  title: string;
  body: string;
  meta: string;
  scriptHint: string | null;
  buttonLabel: string;
};

function getGuideStepCallScript(step: GuideRailSummaryStepLike) {
  if (typeof step.callScript === 'string' && step.callScript.trim()) {
    return step.callScript;
  }

  if (typeof step.call_script === 'string' && step.call_script.trim()) {
    return step.call_script;
  }

  return null;
}

export function countGuideScripts(items: GuideRailSummaryItemLike[]) {
  return items.reduce((count, item) => {
    if (item.kind !== 'playbook' || !item.steps?.length) {
      return count;
    }

    return count + item.steps.filter((step) => Boolean(getGuideStepCallScript(step))).length;
  }, 0);
}

export function buildGuideRailLaunchSummary(
  section: GuideSurfaceSection,
  items: GuideRailSummaryItemLike[],
): GuideRailLaunchSummary | null {
  if (!items.length) {
    return null;
  }

  const helperPacks = items.filter((item) => item.kind === 'playbook');
  const supportCards = items.filter((item) => item.kind === 'content-card');
  const scriptCount = countGuideScripts(items);
  const featuredItem = helperPacks[0] || supportCards[0];

  if (!featuredItem) {
    return null;
  }

  const metaBits = [
    `${helperPacks.length} helper pack${helperPacks.length === 1 ? '' : 's'}`,
    `${supportCards.length} support card${supportCards.length === 1 ? '' : 's'}`,
  ];
  const scriptHint = scriptCount > 0
    ? `${scriptCount} ready-to-use script${scriptCount === 1 ? '' : 's'} saved in this section.`
    : null;

  if (featuredItem.kind === 'playbook') {
    const firstStep = featuredItem.steps?.[0];
    const firstStepLead = firstStep?.title
      ? `If you only have room for one move, start with “${firstStep.title}”.`
      : `Open this first when you need the clearest next step in ${GUIDE_SURFACE_META[section].label}.`;
    const supportingText = firstStep?.description || featuredItem.description || GUIDE_SURFACE_META[section].helperPackSubtitle;

    return {
      itemId: featuredItem.id,
      itemKind: featuredItem.kind,
      title: featuredItem.title,
      body: `${firstStepLead} ${supportingText}`.trim(),
      meta: metaBits.join(' • '),
      scriptHint,
      buttonLabel: 'Open top guide',
    };
  }

  return {
    itemId: featuredItem.id,
    itemKind: featuredItem.kind,
    title: featuredItem.title,
    body: featuredItem.summary
      || featuredItem.body
      || `Open this first for the quickest support note and next route in ${GUIDE_SURFACE_META[section].label}.`,
    meta: metaBits.join(' • '),
    scriptHint,
    buttonLabel: 'Open quick guide',
  };
}
